import Api from '../api';

const ITEM_LEAD_TIME = '/item-lead-times';

export type SortDirection = 'asc' | 'desc';

export interface ItemLeadTimePaginationParams {
  page?: number;
  limit?: number;
  sort?: SortDirection;
  search?: string;
}

export interface PaginatedItemLeadTimeResponse<T> {
  items: T[];
  count: number;
}

export interface SubmitItemLeadTimeData {
  earliest_custom_cut_delivery_date: string;
  custom_cut_lead_time_in_weeks: number;
  country_of_origin: string;
  cost_jr: number;
  cost_plus: number;
  exclusivity: string;
  stock_quantity: number;
  stock_ratio: string | null;
  earliest_IHD_date_to_deliver_stock: string | null;
  incoming_stock_quantity: number;
  incoming_stock_date: string | null;
  incoming_stock_ratio: string | null;
  custom_MOQ_jr: number;
  custom_MOQ_plus: number;
  shipment_mode: string;
  comments: string | null;
}

export interface SubmitBulkItemLeadTimeData extends SubmitItemLeadTimeData {
  worksheet_id: string;
}

export interface AddItemLeadTimeCommentData {
  worksheet_id: string;
  comments: string;
}

const ItemLeadTimeAPI = {
  getTodoItemLeadTimes(
    userId: string,
    params: ItemLeadTimePaginationParams = {},
  ) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.search?.trim()) queryParams.set('search', params.search.trim());
    const queryString = queryParams.toString();
    const url = `/users/${userId}${ITEM_LEAD_TIME}/todo${queryString ? `?${queryString}` : ''}`;
    return Api.get(url);
  },

  async getTodoItemLeadTimesExport(userId: string) {
    const allItems: unknown[] = [];
    let page = 1;
    const limit = 100;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const response = await this.getTodoItemLeadTimes(userId, {
        page,
        limit,
      });

      // Validate response shape
      const items = Array.isArray(response?.items) ? response.items : [];
      const count =
        typeof response?.count === 'number' ? response.count : items.length;

      // If no items returned, stop (prevents infinite loop)
      if (items.length === 0) {
        break;
      }

      allItems.push(...items);

      // Stop if we've fetched all items
      if (allItems.length >= count) {
        break;
      }

      page += 1;
    }

    return allItems;
  },

  getOpenChatItemLeadTimes(
    userId: string,
    params: ItemLeadTimePaginationParams = {},
  ) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.search?.trim()) queryParams.set('search', params.search.trim());
    const queryString = queryParams.toString();
    const url = `/users/${userId}${ITEM_LEAD_TIME}/open-chats${queryString ? `?${queryString}` : ''}`;
    return Api.get(url);
  },

  getCompletedItemLeadTimes(
    userId: string,
    params: ItemLeadTimePaginationParams = {},
  ) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.search?.trim()) queryParams.set('search', params.search.trim());
    const queryString = queryParams.toString();
    const url = `/users/${userId}${ITEM_LEAD_TIME}/completed${queryString ? `?${queryString}` : ''}`;
    return Api.get(url);
  },

  async getCompletedItemLeadTimesExport(userId: string) {
    const allItems: unknown[] = [];
    let page = 1;
    const limit = 100;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const response = await this.getCompletedItemLeadTimes(userId, {
        page,
        limit,
      });

      // Validate response shape
      const items = Array.isArray(response?.items) ? response.items : [];
      const count =
        typeof response?.count === 'number' ? response.count : items.length;

      // If no items returned, stop (prevents infinite loop)
      if (items.length === 0) {
        break;
      }

      allItems.push(...items);

      // Stop if we've fetched all items
      if (allItems.length >= count) {
        break;
      }

      page += 1;
    }

    return allItems;
  },

  submitItemLeadTime(
    userId: string,
    worksheetId: string,
    data: SubmitItemLeadTimeData,
  ) {
    return Api.put(`/users/${userId}${ITEM_LEAD_TIME}/${worksheetId}`, {
      data,
    });
  },

  addItemLeadTimeComment(userId: string, data: AddItemLeadTimeCommentData) {
    return Api.patch(`/users/${userId}${ITEM_LEAD_TIME}/completed/comment`, {
      data,
    });
  },

  bulkUpdateItemLeadTimes(userId: string, items: SubmitBulkItemLeadTimeData[]) {
    return Api.put(`/users/${userId}${ITEM_LEAD_TIME}`, { data: items });
  },

  saveItemLeadTimes(userId: string, items: SubmitBulkItemLeadTimeData[]) {
    return Api.put(`/users/${userId}${ITEM_LEAD_TIME}/todo/save`, {
      data: items,
    });
  },
};

export default ItemLeadTimeAPI;
